import { apiFetch } from "@/lib/api"
import { API_BASE } from "@/lib/config"

/**
 * Client of /v1/accounting — the account's ledger connections
 * (docs/integrations/microsoft/business-central.md). Travel Code posts its
 * invoices and approved reimbursements; nothing is read back but reference lists.
 */

/** Catalog ids served by our own accounting backend instead of the generic catalog flow. */
export const ACCOUNTING_PROVIDER_IDS = ["microsoft-business-central"] as const

export function isAccountingProvider(id: string): boolean {
  return (ACCOUNTING_PROVIDER_IDS as readonly string[]).includes(id)
}

export const ACCOUNTING_VIEWS = ["overview", "settings", "activity"] as const

export type AccountingView = (typeof ACCOUNTING_VIEWS)[number]

export type AccountingKind = "flight" | "hotel" | "train" | "car" | "taxi" | "meals" | "other"

export type AccountingSettings = {
  companyId: string | null
  companyName: string | null
  agencyIds: number[]
  invoices: boolean
  invoicesSince: number | null
  vendorNumber: string | null
  invoiceAccounts: Partial<Record<AccountingKind, string>>
  postInvoices: boolean
  expenses: boolean
  expensesSince: number | null
  expenseAccounts: Partial<Record<AccountingKind, string>>
  journalCode: string | null
  employeeMap: Record<string, string>
  costCenterDimension: string | null
  /** Our VAT rate ("20.00") => the ledger's tax code. */
  vatCodes: Record<string, string>
}

export type ExportKind = "invoice" | "credit" | "expense"

export type ExportCounts = Partial<Record<"sent" | "failed" | "skipped", number>>

export type AccountingConnection = {
  id: number
  provider: string
  providerName: string
  status: "draft" | "active" | "paused" | "error"
  tenantId: string
  environment: string | null
  settings: AccountingSettings
  lastExportAt: number | null
  lastError: string | null
  counts: Partial<Record<ExportKind, ExportCounts>>
}

export type LedgerCompany = { id: string; name: string }

export type AccountingSetup = {
  vendors: { number: string; name: string }[]
  accounts: { number: string; name: string; category: string }[]
  journals: { code: string; name: string }[]
  dimensions: { code: string; name: string }[]
  employees: { number: string; name: string; email: string }[]
  taxGroups: { code: string; name: string; type: string }[]
  /** Rates of our own VAT on the account's recent orders. */
  vatRates: string[]
  currency: string
  kinds: AccountingKind[]
  suggestedAccounts: Partial<Record<AccountingKind, string>>
  agencies: { id: number; name: string }[]
  travellers: { id: number; name: string; email: string; suggested: string | null }[]
}

export type AccountingExportRow = {
  id: number
  kind: ExportKind
  sourceId: number
  reference: string | null
  amount: number | null
  currency: string | null
  status: "sent" | "failed" | "skipped"
  externalNumber: string | null
  error: string | null
  attempts: number
  updatedAt: number
}

export class AccountingApiError extends Error {
  fields: Record<string, string[]>
  status: number

  constructor(message: string, fields: Record<string, string[]> = {}, status = 0) {
    super(message)
    this.fields = fields
    this.status = status
  }
}

const BASE = `${API_BASE}/accounting`

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(`${BASE}${path}`, {
    ...init,
    headers: { Accept: "application/json", "Content-Type": "application/json", ...(init?.headers ?? {}) },
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const fields: Record<string, string[]> = body?.errors ?? {}
    const first = Object.values(fields)[0]?.[0]
    throw new AccountingApiError(first ?? body?.message ?? `Request failed (${res.status})`, fields, res.status)
  }
  return body as T
}

export function fetchConnections(): Promise<AccountingConnection[]> {
  return request("/connections")
}

/**
 * Microsoft's admin consent link. Whoever administers the customer's Microsoft
 * tenant may open it in any browser; the approval lands on this account.
 */
export function startConsent(): Promise<{ consentUrl: string }> {
  return request("/oauth/business-central/start", { method: "POST" })
}

export function fetchCompanies(id: number): Promise<LedgerCompany[]> {
  return request(`/connections/${id}/companies`)
}

export function fetchSetup(id: number, companyId?: string): Promise<AccountingSetup> {
  return request(`/connections/${id}/setup${companyId ? `?companyId=${encodeURIComponent(companyId)}` : ""}`)
}

export function updateSettings(id: number, settings: Partial<AccountingSettings>): Promise<AccountingConnection> {
  return request(`/connections/${id}`, { method: "PUT", body: JSON.stringify({ settings }) })
}

export function activate(id: number): Promise<AccountingConnection> {
  return request(`/connections/${id}/activate`, { method: "POST" })
}

export function pause(id: number): Promise<AccountingConnection> {
  return request(`/connections/${id}/pause`, { method: "POST" })
}

export function disconnect(id: number): Promise<{ ok: boolean }> {
  return request(`/connections/${id}`, { method: "DELETE" })
}

export function fetchExports(id: number, kind?: ExportKind): Promise<AccountingExportRow[]> {
  return request(`/connections/${id}/exports${kind ? `?kind=${kind}` : ""}`)
}

export function runNow(id: number): Promise<{ queued: boolean }> {
  return request(`/connections/${id}/run`, { method: "POST" })
}
